import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

// MeetingReminderBell — Iqra's 30-minute "ding" for upcoming meetings.
//
// Behaviour:
//   • Polls GET /api/meetings/ every 60s for the signed-in user's meetings.
//   • For every meeting whose proposed_at falls inside the next REMINDER_WINDOW_MIN,
//     fires ONCE per meeting (per browser session) — plays a soft chime via
//     Web Audio API + drops a sonner toast: "Iqra · Meeting in N min — {title}".
//   • Notified IDs are persisted to sessionStorage so a tab refresh doesn't
//     re-ring the same meeting.
//   • Mute toggle next to the bell (preference saved in localStorage).
//
// No audio file dependency — chime is synthesised live with OscillatorNode.

const REMINDER_WINDOW_MIN = 30;
const POLL_MS = 60_000;
const NOTIFIED_KEY = 've_meeting_reminded_ids';
const MUTE_KEY = 've_meeting_chime_muted';

function loadNotifiedIds() {
  try { return new Set(JSON.parse(sessionStorage.getItem(NOTIFIED_KEY) || '[]')); }
  catch { return new Set(); }
}
function saveNotifiedIds(set) {
  try { sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify([...set])); } catch { /* noop */ }
}

// Synthesise a 2-tone gentle chime (E5 → A5) — short, polite, recognisable.
function playChime(muted) {
  if (muted) return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const playTone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.22, now + start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    playTone(659.25, 0, 0.45);   // E5
    playTone(880.00, 0.18, 0.55); // A5
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1500);
  } catch { /* audio blocked / unsupported — silently skip */ }
}

export default function MeetingReminderBell() {
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
  });
  const [upcoming, setUpcoming] = useState([]); // meetings inside next REMINDER_WINDOW
  const [open, setOpen] = useState(false);
  const notifiedRef = useRef(loadNotifiedIds());

  // Persist mute preference
  useEffect(() => {
    try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* noop */ }
  }, [muted]);

  // Poll meetings every minute, fire reminders for ones falling inside the
  // next 30 minutes that we haven't notified yet.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/meetings/');
        if (cancelled || !Array.isArray(data)) return;
        const now = Date.now();
        const windowEnd = now + REMINDER_WINDOW_MIN * 60_000;
        const next30 = [];
        const toRing = [];
        for (const m of data) {
          if (!m?.proposed_at) continue;
          if (m.status && ['cancelled', 'rejected', 'declined'].includes(m.status)) continue;
          const t = new Date(m.proposed_at).getTime();
          if (!t || Number.isNaN(t)) continue;
          // Only meetings happening between now and 30 min from now,
          // AND not already started.
          if (t >= now && t <= windowEnd) {
            next30.push(m);
            if (!notifiedRef.current.has(m.meeting_id)) {
              toRing.push(m);
            }
          }
        }
        // Sort by soonest
        next30.sort((a, b) => new Date(a.proposed_at) - new Date(b.proposed_at));
        setUpcoming(next30);

        if (toRing.length) {
          playChime(muted);
          for (const m of toRing) {
            notifiedRef.current.add(m.meeting_id);
            const mins = Math.max(1, Math.round((new Date(m.proposed_at).getTime() - now) / 60_000));
            toast(`Iqra · meeting in ${mins} min`, {
              description: m.title || 'Upcoming meeting',
              duration: 8000,
              action: {
                label: 'View',
                onClick: () => { window.location.href = '/dashboard/meetings'; },
              },
            });
          }
          saveNotifiedIds(notifiedRef.current);
        }
      } catch { /* silently ignore — bell is best-effort */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [muted]);

  const count = upcoming.length;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="meeting-reminder-bell"
        className="relative p-2 text-slate-500 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition-colors rounded-md hover:bg-[#FFF8E5]/60 dark:hover:bg-[#3a2f0a]/40"
        title={count > 0 ? `${count} meeting${count > 1 ? 's' : ''} in next ${REMINDER_WINDOW_MIN} min` : 'No meetings in next 30 minutes'}
        aria-label="Upcoming meeting reminders"
      >
        <CalendarDays size={18} />
        {count > 0 && (
          <span
            data-testid="meeting-reminder-badge"
            className="absolute -top-0.5 -right-0.5 text-[9px] font-bold px-1 min-w-[16px] h-[16px] inline-flex items-center justify-center rounded-full bg-[#5E1F8B] text-white animate-pulse"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 ve-card p-4 z-50 shadow-2xl"
          data-testid="meeting-reminder-panel"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-center justify-between">
            <div className="ve-eyebrow">Iqra · Next 30 min</div>
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              data-testid="meeting-reminder-mute"
              className="text-slate-400 hover:text-[#5E1F8B] transition-colors"
              title={muted ? 'Unmute chime' : 'Mute chime'}
            >
              {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
            </button>
          </div>
          {count === 0 ? (
            <div className="mt-3 text-xs text-slate-500 leading-relaxed">
              No meetings in the next 30 minutes. Iqra will ring once when one is approaching.
            </div>
          ) : (
            <div className="mt-3 space-y-2 max-h-72 overflow-y-auto pr-1">
              {upcoming.map((m) => {
                /* eslint-disable -- v6 react-hooks/purity false-positive on Date.now() for relative-time display */
                const t = new Date(m.proposed_at);
                const mins = Math.max(0, Math.round((t.getTime() - Date.now()) / 60_000));
                /* eslint-enable */
                return (
                  <Link
                    key={m.meeting_id}
                    to="/dashboard/meetings"
                    onClick={() => setOpen(false)}
                    data-testid={`meeting-reminder-row-${m.meeting_id}`}
                    className="block p-2 rounded-md border border-slate-200 hover:border-[#8B3FC5] hover:bg-[#FFF8E5]/40 transition-colors"
                  >
                    <div className="text-sm font-bold leading-tight">{m.title || '(untitled)'}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                      <span className="mono">{t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span>·</span>
                      <span className="text-[#5E1F8B] font-bold">in {mins} min</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-slate-200 text-[10px] text-slate-500">
            Iqra rings a soft chime once per meeting at T-30m. {muted && <span className="text-amber-700 font-bold">Chime muted.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
